import { Box, BoxProps, Image as ChakraImage, ImageProps as ChakraImageProps } from '@cmpsr/components';
import { FC, useEffect, useRef, useState } from 'react';

export interface OptimizedImageProps extends Omit<ChakraImageProps, 'loading'> {
  src: string;
  alt: string;
  width?: string | number;
  height?: string | number;
  priority?: boolean;
  onLoadComplete?: () => void;
  skeletonProps?: BoxProps;
  aspectRatio?: number;
  sizes?: string;
  srcSet?: string;
  sources?: Array<{
    type: string;
    srcSet: string;
    sizes?: string;
  }>;
}

export const OptimizedImage: FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  priority = false,
  onLoadComplete,
  skeletonProps,
  aspectRatio,
  sizes,
  srcSet,
  sources,
  ...imageProps
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (priority || !imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [priority]);


  const handleLoad = () => {
    setIsLoaded(true);
    onLoadComplete?.();
  };

  const handleError = () => {
    // Keep skeleton visible on error
    // Error will be tracked by Datadog RUM automatically
  };

  const containerStyles: BoxProps = {
    position: 'relative',
    width: width || '100%',
    minHeight: !isLoaded ? (skeletonProps?.height || height || '200px') : undefined,
    ...(aspectRatio && {
      paddingBottom: `${(1 / aspectRatio) * 100}%`,
      height: 0,
    }),
  };

  const skeletonStyles: BoxProps = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    bg: 'gray.300',
    borderRadius: 'md',
    animation: 'pulse 2s ease-in-out infinite',
    sx: {
      '@keyframes pulse': {
        '0%': {
          opacity: 0.6,
        },
        '50%': {
          opacity: 1,
        },
        '100%': {
          opacity: 0.6,
        },
      },
    },
    ...skeletonProps,
  };

  const imageStyles: ChakraImageProps = {
    width: '100%',
    height: 'auto',
    opacity: isLoaded ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',
    objectFit: imageProps.objectFit || 'cover',
    objectPosition: imageProps.objectPosition || 'center',
    ...imageProps,
  };

  return (
    <Box ref={imgRef} data-testid="optimized-image-container" {...containerStyles}>
      {!isLoaded && <Box data-testid="optimized-image-skeleton" {...skeletonStyles} />}
      {isInView && (
        sources && sources.length > 0 ? (
          <picture>
            {sources.map((source, index) => (
              <source
                key={index}
                type={source.type}
                srcSet={source.srcSet}
                sizes={source.sizes || sizes}
              />
            ))}
            <ChakraImage
              src={src}
              alt={alt}
              srcSet={srcSet}
              sizes={sizes}
              onLoad={handleLoad}
              onError={handleError}
              loading={priority ? 'eager' : 'lazy'}
              decoding={priority ? 'sync' : 'async'}
              {...imageStyles}
            />
          </picture>
        ) : (
          <ChakraImage
            src={src}
            alt={alt}
            srcSet={srcSet}
            sizes={sizes}
            onLoad={handleLoad}
            onError={handleError}
            loading={priority ? 'eager' : 'lazy'}
            decoding={priority ? 'sync' : 'async'}
            {...imageStyles}
          />
        )
      )}
    </Box>
  );
};